import {
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/react-query'

import type { ProgressCompletionContext } from '../../../dal/progress-dal'
import type { WarmupWeekdayCompletion } from '../../../dal/warmup-dal'
import { ProgressAPI } from '../../../dal/progress-dal'
import { saveWarmupCompletion } from '../../../dal/warmup-dal'
import { useContentfulLocale } from '../../../hooks/useContentfulLocale'
import { queryKeys } from '../../../queries/query-keys'
import { getWeekdayFromDate } from '../../../utils/daily-warmup-completion'
import { consumeSessionFinishEligibility } from '../utils/session-finish-guard'

export interface SessionCompletionVariables {
  sessionId: string
  exerciseType: string | undefined
  context: ProgressCompletionContext
  timeSpent: number
}

const MUTATION_KEY = queryKeys.mutations.sessionCompletion

export function useSessionCompletionMutation(yearWeek: string) {
  const queryClient = useQueryClient()
  const language = useContentfulLocale()

  return useMutation({
    mutationKey: MUTATION_KEY,
    mutationFn: async (variables: SessionCompletionVariables) => {
      const isFinishEligible = await consumeSessionFinishEligibility({
        sessionId: variables.sessionId,
      })
      if (!isFinishEligible) {
        return
      }

      if (variables.exerciseType === 'daily') {
        await saveWarmupCompletion({
          date: new Date(),
          timeSpent: variables.timeSpent,
        })
      } else {
        await ProgressAPI.saveProgress(
          variables.sessionId,
          variables.timeSpent,
          variables.context,
        )
      }
    },
    onMutate: async (variables) => {
      if (variables.exerciseType !== 'daily') {
        return
      }

      await queryClient.cancelQueries({
        queryKey: queryKeys.warmup.weekdayCompletion(yearWeek),
      })

      const previousCompletion =
        queryClient.getQueryData<WarmupWeekdayCompletion | null>(
          queryKeys.warmup.weekdayCompletion(yearWeek),
        )

      const today = getWeekdayFromDate(new Date())
      queryClient.setQueryData<WarmupWeekdayCompletion | null>(
        queryKeys.warmup.weekdayCompletion(yearWeek),
        (old) => (old == null ? old : { ...old, [today]: true }),
      )

      return { previousCompletion }
    },
    onError: (_error, variables, context) => {
      if (variables.exerciseType !== 'daily' || context == null) {
        return
      }

      queryClient.setQueryData(
        queryKeys.warmup.weekdayCompletion(yearWeek),
        context.previousCompletion,
      )
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.progress.snapshotAll(),
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.dashboard.all(language),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.exercise.dashboardInfoAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.exercise.week(yearWeek),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.activities.stats(),
        }),
      ])
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.warmup.weekdayCompletion(yearWeek),
      })
    },
  })
}

export type SessionCompletionStatus = 'idle' | 'pending' | 'success' | 'error'

export function useSessionCompletionStatus(): SessionCompletionStatus {
  const states = useMutationState({
    filters: { mutationKey: MUTATION_KEY },
    select: (mutation) => mutation.state.status,
  })

  const latest = states[states.length - 1]
  return latest ?? 'idle'
}
